import { localDate, PLATFORM_CASH_CATEGORIES, PLATFORM_CASH_METHODS, PLATFORM_TIMEZONE } from "@cafe/shared";
import { Badge, Button, Card, CardHeader, DataTable, dialogs, EmptyState, Field, FormGrid, Input, Modal, MoneyInput, PageHeader, Pagination, SearchSelect, SegmentedControl, Select, Textarea, toast } from "@cafe/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Wallet } from "lucide-react";
import { useState } from "react";
import { ApiError, day, errorText, http, money, useCan } from "../lib/api";
import { CASH_CATEGORY, CASH_METHOD } from "../lib/labels";
import { PeriodFilter, ShareRow, Tile, usePeriod } from "../lib/parts";

type Op = {
  id: string; source: "payment" | "manual"; type: "income" | "expense"; category: string; amount: number; currency: string; method: string; date: string; createdAt: string;
  status: string; comment: string | null; voidReason: string | null; companyId: string | null; companyName: string | null; staffId: string | null; staffName: string | null;
  invoiceId: string | null; invoiceNumber: string | null; createdByName: string | null;
};
type Journal = { currency: string; items: Op[]; totals: { income: number; expense: number; net: number; opening: number; closing: number }; byCategory: { type: string; category: string; total: number; count: number }[]; total: number };
type Form = { type: "income" | "expense"; category: string; amount: number | null; method: string; date: string; comment: string; staffId: string | null; companyId: string | null };
const PAGE = 50;
const NEEDS_STAFF = ["salary", "commission"];

/** Новая операция: приход или расход; зарплата и вознаграждение — с сотрудником, возврат клиенту — с компанией. */
function OpModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const today = localDate(PLATFORM_TIMEZONE);
  const [f, setF] = useState<Form>({ type: "expense", category: "salary", amount: null, method: "bank", date: today, comment: "", staffId: null, companyId: null });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const staff = useQuery({ queryKey: ["staff-options"], queryFn: () => http.get<{ id: string; name: string }[]>("/staff/options") });
  const setType = (type: Form["type"]) => setF({ ...f, type, category: PLATFORM_CASH_CATEGORIES[type][0] });
  const save = async () => {
    setErrors({});
    try {
      await http.post("/cash-operations", { type: f.type, category: f.category, amount: f.amount ?? 0, method: f.method, date: f.date, comment: f.comment.trim() || null, staffId: f.staffId, companyId: f.companyId });
      toast.success("Операция внесена");
      void qc.invalidateQueries({ queryKey: ["cash"] });
      void qc.invalidateQueries({ queryKey: ["staff-finance"] });
      onClose();
    } catch (e) {
      if (e instanceof ApiError && e.fields.length) setErrors(Object.fromEntries(e.fields.map((x) => [x.path, x.message])));
      else toast.error(errorText(e));
    }
  };
  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title="Новая денежная операция" description="Оплаты клиентов по счетам попадают сюда сами — вносить их не нужно."
      footer={<><Button onClick={() => void save()}>Внести</Button><Button variant="outline" onClick={onClose}>Отмена</Button></>}>
      <div className="flex flex-col gap-4">
        <SegmentedControl aria-label="Тип операции" fullWidth value={f.type} onValueChange={setType} options={[{ value: "expense", label: "Расход" }, { value: "income", label: "Приход" }]} />
        <FormGrid cols={2}>
          <Field label="Статья" error={errors.category} required>
            <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })} options={PLATFORM_CASH_CATEGORIES[f.type].map((c) => ({ value: c, label: CASH_CATEGORY[c] ?? c }))} />
          </Field>
          <Field label="Сумма" error={errors.amount} required><MoneyInput value={f.amount} onValueChange={(v) => setF({ ...f, amount: v })} /></Field>
          <Field label="Как" error={errors.method}>
            <Select value={f.method} onValueChange={(v) => setF({ ...f, method: v })} options={PLATFORM_CASH_METHODS.map((m) => ({ value: m, label: CASH_METHOD[m]! }))} />
          </Field>
          <Field label="Дата" error={errors.date} required><Input type="date" value={f.date} max={today} onChange={(e) => setF({ ...f, date: e.target.value })} /></Field>
          <Field label="Сотрудник" error={errors.staffId} required={NEEDS_STAFF.includes(f.category)} hint={NEEDS_STAFF.includes(f.category) ? "Кому выплачено — попадёт в «Финансы сотрудников»" : undefined}>
            <SearchSelect value={f.staffId} onValueChange={(v) => setF({ ...f, staffId: v })} clearable options={(staff.data ?? []).map((u) => ({ value: u.id, label: u.name }))} />
          </Field>
          <Field label="Компания" error={errors.companyId} hint="Если операция связана с клиентом">
            <SearchSelect value={f.companyId} onValueChange={(v) => setF({ ...f, companyId: v })} clearable
              loadOptions={async (q) => (await http.get<{ id: string; name: string }[]>("/companies", { q, pageSize: 20 })).map((c) => ({ value: c.id, label: c.name }))} />
          </Field>
        </FormGrid>
        <Field label="Комментарий" error={errors.comment}><Textarea value={f.comment} onChange={(e) => setF({ ...f, comment: e.target.value })} rows={2} maxLength={500} /></Field>
      </div>
    </Modal>
  );
}

/** Денежные операции платформы: оплаты клиентов (приходят сами) и приходы/расходы, внесённые вручную; остаток на начало и конец периода. */
export default function MoneyPage() {
  const can = useCan();
  const qc = useQueryClient();
  const [period, setPeriod] = usePeriod();
  const [type, setType] = useState<"all" | "income" | "expense">("all");
  const [source, setSource] = useState("all");
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const list = useQuery({
    queryKey: ["cash", period, type, source, page],
    queryFn: () => http.get<Journal>("/cash-operations", { ...period, type: type === "all" ? undefined : type, source: source === "all" ? undefined : source, page, pageSize: PAGE }),
    placeholderData: (prev) => prev,
  });
  const d = list.data;
  const m = (v: number) => money(v, d?.currency);
  const voidOp = (o: Op) =>
    dialogs.confirmDanger({
      title: `Отменить операцию на ${m(o.amount)}?`, description: "Операция останется в журнале с пометкой «отменена» и не будет учитываться в итогах.", confirmText: "Отменить операцию",
      onConfirm: async () => {
        try {
          await http.post(`/cash-operations/${o.id}/void`, { reason: "Отменено в журнале денежных операций" });
          toast.success("Операция отменена");
          void qc.invalidateQueries({ queryKey: ["cash"] });
          void qc.invalidateQueries({ queryKey: ["staff-finance"] });
        } catch (e) {
          toast.error(errorText(e));
        }
      },
    });
  return (
    <>
      <PageHeader title="Денежные операции" description="Все деньги платформы: оплаты клиентов по счетам и расходы — зарплата, вознаграждения, реклама, серверы."
        actions={can("billing.manage") && <Button leftIcon={<Plus />} onClick={() => setOpen(true)}>Внести операцию</Button>} />
      <PeriodFilter value={period} onChange={(p) => { setPeriod(p); setPage(1); }} />
      {d && (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Tile label="На начало периода" value={m(d.totals.opening)} />
          <Tile label="Приход" value={m(d.totals.income)} tone="success" />
          <Tile label="Расход" value={m(d.totals.expense)} tone={d.totals.expense ? "danger" : undefined} />
          <Tile label="На конец периода" value={m(d.totals.closing)} hint={`За период ${d.totals.closing - d.totals.opening >= 0 ? "+" : ""}${m(d.totals.closing - d.totals.opening)}`} />
        </div>
      )}
      {d && d.byCategory.length > 0 && (
        <div className="grid gap-4 lg:grid-cols-2">
          {(["income", "expense"] as const).map((t) => {
            const rows = d.byCategory.filter((c) => c.type === t);
            const total = rows.reduce((a, c) => a + c.total, 0);
            return (
              <Card key={t}>
                <CardHeader title={t === "income" ? "Откуда пришли деньги" : "На что потратили"} />
                <ul className="flex flex-col gap-3 px-5 pb-5">
                  {rows.length ? rows.map((c) => <ShareRow key={c.category} label={`${CASH_CATEGORY[c.category] ?? c.category} · ${c.count}`} value={c.total} total={total} format={m} />) : <p className="text-sm text-muted">Не было</p>}
                </ul>
              </Card>
            );
          })}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <SegmentedControl aria-label="Тип" value={type} onValueChange={(v) => { setType(v); setPage(1); }} options={[{ value: "all", label: "Все" }, { value: "income", label: "Приход" }, { value: "expense", label: "Расход" }]} />
        <Select value={source} onValueChange={(v) => { setSource(v); setPage(1); }} className="sm:w-60"
          options={[{ value: "all", label: "Оплаты и ручные операции" }, { value: "payment", label: "Только оплаты клиентов" }, { value: "manual", label: "Только внесённые вручную" }]} />
      </div>
      <DataTable rows={d?.items} loading={list.isPending} rowKey={(o) => o.id} empty={<EmptyState icon={<Wallet />} title="Операций нет" />}
        columns={[
          { key: "d", header: "Дата", cell: (o) => day(o.date) },
          {
            key: "c", header: "Операция", mobile: "title",
            cell: (o) => (
              <div className={o.status === "void" ? "opacity-60" : ""}>
                <p className="font-medium">{CASH_CATEGORY[o.category] ?? o.category}</p>
                <p className="text-xs text-muted">{[o.companyName, o.staffName, o.invoiceNumber && `счёт ${o.invoiceNumber}`, o.comment].filter(Boolean).join(" · ") || "—"}</p>
              </div>
            ),
          },
          { key: "m", header: "Как", cell: (o) => CASH_METHOD[o.method] ?? o.method },
          {
            key: "a", header: "Сумма", align: "end", mobile: "subtitle",
            cell: (o) => <span className={`font-semibold tabular-nums ${o.status === "void" ? "text-subtle line-through" : o.type === "income" ? "text-success" : "text-danger"}`}>{o.type === "income" ? "+" : "−"}{m(o.amount)}</span>,
          },
          {
            key: "s", header: "Статус",
            cell: (o) => o.status === "posted" ? <Badge tone="success" dot>Проведена</Badge> : o.status === "void" ? <Badge tone="neutral" dot>Отменена</Badge> : <Badge tone="warning" dot>Возврат</Badge>,
          },
          { key: "w", header: "Кто внёс", cell: (o) => o.createdByName ?? (o.source === "payment" ? "автоматически" : "—") },
        ]}
        actions={(o) => can("billing.manage") && o.source === "manual" && o.status === "posted" && <Button size="sm" variant="ghost" onClick={() => void voidOp(o)}>Отменить</Button>} />
      {d && <Pagination page={page} pageSize={PAGE} total={d.total} onPageChange={setPage} />}
      {open && <OpModal onClose={() => setOpen(false)} />}
    </>
  );
}
